import { HeatData, PoolInfo, SeasonMonth } from "../context/types";
import { heatCoefficient, heatCurve } from "../constants/newConst";
import { getTotalPrice } from "../components/ResultStep/utils/calculateValues";
import {
  DegreesPower,
  PumpCapacity,
  PricesData,
} from "../types/context/poolCalculations";
import { pumpModels } from "../constants/pumpData";

export function calculateMaxMachinePowerByMonth(
  months: Record<string, SeasonMonth>,
  maxMachinePowerReal: number[]
): number[] {
  const monthsToArray = Object.values(months);
  return monthsToArray.map((month, index) => {
    if (month.value) {
      return maxMachinePowerReal[index];
    }
    return 0;
  });
}

export function findHighestPowerAndIndex(
  maxMachinePowerByAvailableMonth: number[]
): {
  higher: number;
  higherIndex: number;
} {
  const higher = Math.max(...maxMachinePowerByAvailableMonth);
  const higherIndex = maxMachinePowerByAvailableMonth.indexOf(higher);
  return { higher, higherIndex };
}

export function calculateHeatCoefficient(
  monthTemperatureByCity: Record<string, number>,
  higherIndex: number
): number {
  const Q36 = monthTemperatureByCity[higherIndex + 1];

  return heatCurve.reduce((acc, curve, index) => {
    return acc * (Q36 > curve ? 1 : heatCoefficient[index]);
  }, 1);
}

export function calculateDegreesPower(
  higher: number,
  finalHeatCoefficient: number
): DegreesPower {
  const maxHeatLossSmall = higher / finalHeatCoefficient;
  const maxHeatLossBig = higher * 1.5;

  return {
    small: maxHeatLossSmall,
    big: maxHeatLossBig,
  };
}

export function calculatePumpCapacity(
  maxHeatLossSmall: number,
  usesLowTemperatureCOP: boolean = false
): PumpCapacity {
  const availablePumps = pumpModels.map((pump: any) => {
    const capacity = pump.onAir26?.capacity || pump.onAir15.capacity;

    return {
      model: pump.model,
      capacity: capacity,
      type: pump.type,
    };
  });

  availablePumps.sort((a: any, b: any) => a.capacity - b.capacity);

  const suitableSinglePump = availablePumps.find(
    (pump: any) => pump.capacity >= maxHeatLossSmall
  );

  let standardCapacity: number;
  let pumpCounter: number;

  if (suitableSinglePump) {
    standardCapacity = suitableSinglePump.capacity;
    pumpCounter = 1;
  } else {
    const suitableDoublePump = availablePumps.find(
      (pump: any) => pump.capacity * 2 >= maxHeatLossSmall
    );

    if (suitableDoublePump) {
      standardCapacity = suitableDoublePump.capacity;
      pumpCounter = 2;
    } else {
      const largestPump = availablePumps[availablePumps.length - 1];
      standardCapacity = largestPump?.capacity || 0;
      pumpCounter = 3;
    }
  }

  const PREMIUM_PUMP_MAPPING_26C: Record<number, number> = {
    0: 0,
    9.0: 11.5, // DAT090 -> DAT115
    9.3: 11.5, // DAT093 -> DAT115
    10.9: 14.0,
    11.5: 14.0, // DAT115 -> DAT140
    12.5: 14.0, // DAT125 -> DAT140
    14.0: 17.0, // DAT140 -> DAT170
    17.0: 22.5, // DAT170 -> DAT225
    22.5: 26.5, // DAT225 -> DAT265
    26.5: 26.5, // DAT265 -> DAT265 (maximum pump)
  };

  const premiumCapacity =
    PREMIUM_PUMP_MAPPING_26C[standardCapacity] ||
    // If no specific mapping exists, find the next pump with higher capacity
    (() => {
      const nextHigherPump = availablePumps.find(
        (pump: any) => pump.capacity > standardCapacity
      );
      return nextHigherPump?.capacity || standardCapacity;
    })();

  return {
    standart: standardCapacity,
    premium: premiumCapacity,
    counter: pumpCounter,
  };
}

export function calculatePricesData(
  heatData: HeatData,
  months: Record<string, SeasonMonth>,
  pumpCapacity: PumpCapacity,
  poolInfo: PoolInfo,
  usesLowTemperatureCOP?: boolean
): PricesData {
  const monthsToArray = Object.values(months);

  const {
    premiumCostPerMonth,
    standardCostPerMonth,
    totalCostPremium,
    totalCostStandard,
    bigModelEnergyConsumption,
    smallModelEnergyConsumption,
  } = getTotalPrice({
    heatData,
    months: monthsToArray,
    standartPumpCapacity: pumpCapacity.standart,
    premiumPumpCapacity: pumpCapacity.premium,
    poolInfo,
    counter: pumpCapacity.counter,
    usesLowTemperatureCOP,
  });

  return {
    premiumPricePerMonth: premiumCostPerMonth,
    standartPricePerMonth: standardCostPerMonth,
    totalPremiumPrice: totalCostPremium,
    totalStandartPrice: totalCostStandard,
    smallConsumption: smallModelEnergyConsumption,
    bigConsumption: bigModelEnergyConsumption,
  };
}
